import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Phone, Mail, MapPin, ArrowLeft, Sparkles, Globe, Instagram, Facebook, Clock, Award, Leaf, BadgeCheck } from 'lucide-react';
import { API } from '../App';
import ListingCard from '../components/ListingCard';
import { devError } from '../utils/logger';

const FONT_PRELOAD_LINKS = {
  'Playfair Display': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&display=swap',
  'Cinzel': 'https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&display=swap',
  'Marcellus': 'https://fonts.googleapis.com/css2?family=Marcellus&display=swap',
  'Cormorant Garamond': 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&display=swap',
  'UnifrakturMaguntia': 'https://fonts.googleapis.com/css2?family=UnifrakturMaguntia&display=swap',
  'UnifrakturCook': 'https://fonts.googleapis.com/css2?family=UnifrakturCook:wght@700&display=swap',
  'Pirata One': 'https://fonts.googleapis.com/css2?family=Pirata+One&display=swap',
  'MedievalSharp': 'https://fonts.googleapis.com/css2?family=MedievalSharp&display=swap',
};

const ensureFont = (font) => {
  if (!font) return;
  const url = FONT_PRELOAD_LINKS[font];
  if (!url) return;
  if (document.querySelector(`link[data-font="${font}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.dataset.font = font;
  document.head.appendChild(link);
};

/**
 * Public-facing supplier storefront page.
 *
 * Routes:
 *   - `/s/:slug` (path-based, works on patinamart.com today)
 *   - `{slug}.patinamart.com` (subdomain detection in App.js redirects to /s/:slug)
 */
export default function StorefrontPage() {
  const params = useParams();
  const slug = params.slug;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/storefront/${slug}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        devError('Storefront load failed:', e);
        if (!cancelled) setError(e.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  // Inject the chosen logo font once the storefront data arrives.
  useEffect(() => {
    if (data?.logo_font) ensureFont(data.logo_font);
  }, [data?.logo_font]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="storefront-loading">
        <Loader2 className="w-10 h-10 animate-spin text-accent" />
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center" data-testid="storefront-404">
        <h1 className="font-playfair text-3xl mb-3">Storefront not found</h1>
        <p className="text-muted-foreground mb-6">No supplier is using <code>{slug}</code> as their storefront URL.</p>
        <Link to="/marketplace" className="text-accent hover:underline">Browse the marketplace →</Link>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <h1 className="font-playfair text-3xl mb-3">Couldn't load this storefront</h1>
        <p className="text-muted-foreground">Please try again in a moment.</p>
      </div>
    );
  }

  const accent = data.accent_color || '#c9a961';
  const accentRgb = hexToRgb(accent);
  const cssVars = {
    '--storefront-accent': accent,
    '--storefront-accent-soft': `rgba(${accentRgb}, 0.12)`,
  };

  return (
    <div className="min-h-screen bg-background" style={cssVars} data-testid="storefront-page">
      {/* Banner / hero */}
      <div
        className="relative w-full overflow-hidden"
        style={{
          background: data.banner_url
            ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.45)), url(${data.banner_url}) center/cover`
            : `linear-gradient(135deg, ${accent}33, ${accent}11)`,
          minHeight: '320px',
        }}
      >
        <div className="absolute top-4 left-4">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white text-xs backdrop-blur-sm transition-colors"
            data-testid="storefront-back-to-marketplace"
          >
            <ArrowLeft className="w-3 h-3" />
            PatinaMart Marketplace
          </Link>
        </div>

        <div className="container mx-auto px-6 pt-20 pb-12 flex flex-col items-center text-center">
          {data.logo_url ? (
            <img
              src={data.logo_url}
              alt={data.company_name}
              className="max-w-full w-auto h-auto max-h-48 md:max-h-56 mb-4 rounded-md shadow-2xl"
              style={{ objectFit: 'contain' }}
              data-testid="storefront-logo"
            />
          ) : (
            <h1
              className={`text-4xl md:text-5xl lg:text-6xl text-white drop-shadow-lg mb-3 ${
                /Unifraktur|Pirata|MedievalSharp/i.test(data.logo_font || '')
                  ? 'font-normal tracking-normal'
                  : 'font-bold tracking-tight'
              }`}
              style={{ fontFamily: `"${data.logo_font || 'Playfair Display'}", Georgia, serif` }}
              data-testid="storefront-name"
            >
              {data.logo_text || data.company_name}
            </h1>
          )}
          {data.is_verified_seller && (
            <Link
              to="/trust"
              className="mb-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 border border-white/30 backdrop-blur-sm text-white text-[11px] uppercase tracking-wider font-medium hover:bg-white/25 transition-colors"
              title="PatinaMart has manually verified this seller — click for details"
              data-testid="storefront-verified-badge"
            >
              <BadgeCheck className="w-3.5 h-3.5" />
              Verified seller
            </Link>
          )}
          {data.tagline && (
            <p className="text-base md:text-lg text-white/90 max-w-2xl drop-shadow tracking-wider uppercase text-[11px] md:text-xs" data-testid="storefront-tagline">
              {data.tagline}
            </p>
          )}
          {data.established_year && (
            <p className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white/90 text-xs">
              <Award className="w-3 h-3" />
              Trading since {data.established_year}
            </p>
          )}
          {data.location && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-white/80 text-sm">
              <MapPin className="w-4 h-4" />
              {data.location}
            </p>
          )}
        </div>
      </div>

      {/* Stats bar */}
      <div
        className="border-b border-border/30"
        style={{ backgroundColor: 'var(--storefront-accent-soft)' }}
      >
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm">
          <div>
            <span className="font-semibold" style={{ color: accent }} data-testid="storefront-listing-count">
              {data.listing_count}
            </span>{' '}
            <span className="text-muted-foreground">items in stock</span>
          </div>
          {data.featured_listings.length > 0 && (
            <div>
              <span className="font-semibold" style={{ color: accent }}>
                {data.featured_listings.length}
              </span>{' '}
              <span className="text-muted-foreground">featured pieces</span>
            </div>
          )}
          {data.carbon_saved_kg > 0 && (
            <div
              className="inline-flex items-center gap-1.5 text-emerald-300"
              title="Total estimated embodied-carbon (kg CO₂e) avoided across this supplier's available stock vs. buying new — Bath ICE v3.0"
              data-testid="storefront-carbon-total"
            >
              <Leaf className="w-3.5 h-3.5" />
              <span className="font-semibold">
                {data.carbon_saved_tonnes >= 1
                  ? `${data.carbon_saved_tonnes.toFixed(1)} t`
                  : `${Math.round(data.carbon_saved_kg)} kg`}
              </span>{' '}
              <span className="text-emerald-300/70">CO₂e saved</span>
            </div>
          )}
        </div>
      </div>

      {/* About */}
      {data.about && (
        <section className="container mx-auto px-6 py-12 max-w-4xl" data-testid="storefront-about">
          <h2 className="font-playfair text-2xl md:text-3xl mb-4" style={{ color: accent }}>
            About {data.company_name}
          </h2>
          <p className="text-foreground/80 leading-relaxed whitespace-pre-line">{data.about}</p>

          {/* Heritage gallery — scraped photos showing the showroom, owner, family, restoration craft. */}
          {data.heritage_images?.length > 0 && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="storefront-heritage-gallery">
              {data.heritage_images.map((img, idx) => (
                <figure key={img.url || idx} className="group relative overflow-hidden rounded-lg border border-border/30 bg-card/30">
                  <img
                    src={img.url}
                    alt={img.caption || ''}
                    loading="lazy"
                    className="w-full h-48 md:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {img.caption && (
                    <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/55 to-transparent text-white text-xs leading-snug p-3 pt-8 opacity-0 group-hover:opacity-100 transition-opacity">
                      {img.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Specialties + Brands */}
      {(data.specialties?.length > 0 || data.partner_brands?.length > 0) && (
        <section className="container mx-auto px-6 pb-8 max-w-4xl" data-testid="storefront-specialties">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {data.specialties?.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Specialties</h3>
                <ul className="space-y-1.5">
                  {data.specialties.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-sm text-foreground/80">
                      <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: accent }} />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.partner_brands?.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Partner brands</h3>
                <div className="flex flex-wrap gap-2">
                  {data.partner_brands.map((b) => (
                    <span
                      key={b}
                      className="px-3 py-1 rounded-full text-xs"
                      style={{ backgroundColor: 'var(--storefront-accent-soft)', color: accent }}
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Visit & Contact */}
      {(data.public_address || data.public_phone || data.public_email || data.hours || data.website) && (
        <section className="container mx-auto px-6 py-12 max-w-4xl" data-testid="storefront-contact">
          <div className="rounded-xl border border-border/30 bg-card/30 p-6 md:p-8">
            <h2 className="font-playfair text-2xl md:text-3xl mb-6" style={{ color: accent }}>
              Visit us
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
              {data.public_address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: accent }} />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Showroom</p>
                    <p className="text-foreground/90">{data.public_address}</p>
                  </div>
                </div>
              )}
              {data.public_phone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: accent }} />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Phone</p>
                    <a href={`tel:${data.public_phone.replace(/\s/g, '')}`} className="text-foreground/90 hover:underline" data-testid="storefront-phone">
                      {data.public_phone}
                    </a>
                  </div>
                </div>
              )}
              {data.public_email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: accent }} />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Email</p>
                    <a href={`mailto:${data.public_email}`} className="text-foreground/90 hover:underline" data-testid="storefront-email">
                      {data.public_email}
                    </a>
                  </div>
                </div>
              )}
              {data.hours && (
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: accent }} />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Opening hours</p>
                    <ul className="text-sm text-foreground/90 space-y-0.5">
                      {['mon','tue','wed','thu','fri','sat','sun'].map((d) => data.hours[d] && (
                        <li key={d}><span className="inline-block w-10 text-muted-foreground capitalize">{d}</span>{data.hours[d]}</li>
                      ))}
                    </ul>
                    {data.hours.note && <p className="text-xs text-muted-foreground mt-2">{data.hours.note}</p>}
                  </div>
                </div>
              )}
            </div>

            {(data.website || data.instagram || data.facebook) && (
              <div className="mt-6 pt-6 border-t border-border/15 flex flex-wrap items-center gap-3">
                {data.website && (
                  <a href={data.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-accent" data-testid="storefront-website">
                    <Globe className="w-3.5 h-3.5" />
                    {data.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                {data.instagram && (
                  <a href={data.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-accent">
                    <Instagram className="w-3.5 h-3.5" />
                    Instagram
                  </a>
                )}
                {data.facebook && (
                  <a href={data.facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-accent">
                    <Facebook className="w-3.5 h-3.5" />
                    Facebook
                  </a>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured */}
      {data.featured_listings.length > 0 && (
        <section className="container mx-auto px-6 py-12">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5" style={{ color: accent }} />
            <h2 className="font-playfair text-2xl md:text-3xl" style={{ color: accent }}>
              Featured pieces
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.featured_listings.map((l) => (
              <ListingCard key={l.listing_id} listing={l} />
            ))}
          </div>
        </section>
      )}

      {/* All listings */}
      <section className="container mx-auto px-6 py-12">
        <h2 className="font-playfair text-2xl md:text-3xl mb-6" style={{ color: accent }} data-testid="storefront-listings-heading">
          Browse our materials
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="storefront-listings-grid">
          {data.listings.map((l) => (
            <ListingCard key={l.listing_id} listing={l} />
          ))}
        </div>
        {data.listings.length === 0 && (
          <p className="text-center text-muted-foreground py-12">No listings available right now. Check back soon.</p>
        )}
      </section>

      {/* Contact */}
      {(data.public_phone || data.public_email || data.public_address) && (
        <section
          className="border-t border-border/30 py-12"
          style={{ backgroundColor: 'var(--storefront-accent-soft)' }}
        >
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <h2 className="font-playfair text-2xl md:text-3xl mb-6" style={{ color: accent }}>
              Get in touch
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm">
              {data.public_phone && (
                <a href={`tel:${data.public_phone}`} className="inline-flex items-center gap-2 hover:underline" data-testid="storefront-phone">
                  <Phone className="w-4 h-4" />
                  {data.public_phone}
                </a>
              )}
              {data.public_email && (
                <a href={`mailto:${data.public_email}`} className="inline-flex items-center gap-2 hover:underline" data-testid="storefront-email">
                  <Mail className="w-4 h-4" />
                  {data.public_email}
                </a>
              )}
              {data.public_address && (
                <span className="inline-flex items-center gap-2 text-foreground/80" data-testid="storefront-address">
                  <MapPin className="w-4 h-4" />
                  {data.public_address}
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      {/* PatinaMart footer */}
      <footer className="border-t border-border/30 py-6 text-center text-xs text-muted-foreground">
        Powered by{' '}
        <Link to="/" className="hover:text-foreground transition-colors">
          PatinaMart
        </Link>
        {' '}— circular economy for building materials
      </footer>
    </div>
  );
}

function hexToRgb(hex) {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return '201, 169, 97';
  return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`;
}
